import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, Output, OnDestroy } from '@angular/core';
import { ModalComponent } from '../../../ui/modal-component/modal-component.component';
import { FormWrapperComponent } from '../../../ui/form-wrapper/form-wrapper.component';
import { InputFieldComponent } from '../../../ui/inputField/inputField.component';
import { BkSelectComponent } from '../../../inputs/bk-select/bk-select.component';
import { MessageAttributeMenuComponent } from '../../../ui/message-attribute-menu/message-attribute-menu.component';
import { MessageAttributeChipsComponent } from '../../../ui/message-attribute-chips/message-attribute-chips.component';
import {
  extractMessageAttributes,
  removeMessageAttribute,
} from '../../../../../utils/utils';
import { MessageSchedulesComponent } from '../create-message/message-schedules/message-schedules.component';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { describeChar, smsParts } from '../../../../../utils/sms';
import {
  CampaignDetails,
  EngagementMessage,
} from '../../../../pages/organization/campaigns/types/campaignDetails';
import { CustomizableButtonComponent } from '../../../ui/customizable-button/customizable-button.component';
import { CampaignsService } from '../../../../services/campaign/campaigns-api.service';
import { MessageTemplate } from '../../../../services/message-templates/message.template';
import { BroadcastMessageService } from '../../../../services/broadcast/broadcast.service';
import { ISenderID } from '../../../../services/broadcast/types/broadcastTypes';

@Component({
  selector: 'app-edit-message',
  standalone: true,
  imports: [
    CommonModule,
    ModalComponent,
    FormWrapperComponent,
    InputFieldComponent,
    BkSelectComponent,
    MessageAttributeMenuComponent,
    MessageAttributeChipsComponent,
    MessageSchedulesComponent,
    ReactiveFormsModule,
    CustomizableButtonComponent,
  ],
  templateUrl: './edit-message.component.html',
  styleUrl: '../campaign-form.css',
})
export class EditMessageComponent implements OnDestroy {
  @Output() onClose = new EventEmitter();
  @Input({ required: true }) openModal = false;
  @Input() message!: EngagementMessage;
  campaignsService = inject(CampaignsService);
  @Input() campaignData!: CampaignDetails;
  templateService = inject(MessageTemplate);
  broadcastService = inject(BroadcastMessageService);
  templates: any = [];
  senderIDs: ISenderID[] = [];
  private timeoutId: ReturnType<typeof setTimeout> | undefined;

  constructor(private fb: FormBuilder) {}

  messagesForm = this.fb.group({
    senderId: ['', Validators.required],
    subject: ['', Validators.required],
    template: '',
    body: ['', Validators.required],
  });

  submitted = false;

  get senderId() {
    return this.messagesForm.get('senderId');
  }

  get subject() {
    return this.messagesForm.get('subject');
  }

  get body() {
    return this.messagesForm.get('body');
  }

  /** "84 characters · 1 part", with the character that forced Unicode when there is one. */
  get partsLabel(): string {
    const text = String(this.body?.value ?? '');
    if (!text) return 'Up to 160 characters fit in one SMS part.';
    const { length, parts, encoding, offender, estimated } = smsParts(text);
    const count = `${estimated ? 'About ' : ''}${length} characters · ${parts === 1 ? '1 part' : parts + ' parts'}`;
    if (encoding !== 'Unicode') return count;
    return `${count} · Unicode${offender ? ', because of ' + describeChar(offender) : ''}`;
  }

  // remove a contact-attribute placeholder from the message body (chip dismiss)
  removeAttribute(attribute: string) {
    this.messagesForm.controls['body'].setValue(
      removeMessageAttribute(this.body?.value, attribute)
    );
  }

  onSubmit() {
    this.submitted = true;
    if (this.messagesForm.invalid) return;
    // find message with id
    const messageData = this.campaignData.engagementMessages.find(
      (item) => item.id === this.message.id
    );

    // contact attributes embedded as `{{attribute}}` placeholders in the body
    const attributes = extractMessageAttributes(this.body?.value);

    const data = {
      ...this.campaignData,
      engagementMessages: [
        ...this.campaignData.engagementMessages.filter(
          (item) => item.id !== this.message.id
        ),

        // replace message with edited message
        {
          ...messageData,
          messageSubject: this.subject?.value,
          content: this.body?.value,
          hasAttribute: attributes.length > 0,
          attributes,
        },
      ],
    };
    this.campaignsService
      .editCampaign(this.campaignData.id, data, false)
      .subscribe(() => {
        this.onClose.emit();
      });
  }

  /** The picker hands over the template id; its text replaces the message body. */
  handleSelectTemplate(templateId: string) {
    const selectedTemplate = this.templates.find((item: any) => String(item.id) === templateId);
    if (!selectedTemplate) return;

    this.messagesForm.patchValue({
      body: selectedTemplate.content,
    });
  }

  // appendAttribute(name: string) {
  //   this.body?.setValue(this.body + ' ' + `{${name}}`);
  // }

  appendAttribute(textarea: HTMLTextAreaElement, attribute: string) {
    const template = `{{${attribute}}}`;

    const start = textarea.selectionStart; // cursor start
    const end = textarea.selectionEnd; // cursor end (same if no selection)

    // Build new string: before + template + after
    const messageBody = this.messagesForm.controls['body'].value || '';
    const before = messageBody.substring(0, start);
    const after = messageBody.substring(end);
    this.messagesForm.controls['body'].setValue(before + template + after);

    // Move cursor to the end of the inserted text
    const newPos = start + template.length;

    // DOM update is async – use setTimeout (or requestAnimationFrame)
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    this.timeoutId = setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newPos, newPos);
      this.timeoutId = undefined;
    }, 0);
  }

  ngOnInit() {
    this.messagesForm.patchValue({
      subject: this.message.messageSubject,
      body: this.message.content,
    });

    // fetch the templates
    this.templateService.fetchTemplates();
    this.templateService.getTemplates.subscribe((data) => {
      this.templates = data;
    });

    // fetch sender IDs
    this.broadcastService.fetchSenderIDs().subscribe();
    this.broadcastService.getSenderIDs$.subscribe({
      next: (value) => {
        this.senderIDs = value;
      },
    });
  }

  ngOnDestroy() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  }
}
